import { CadenceType } from '../StructureEngine';

/**
 * PhraseBuilder
 * P2 FIX: Enhanced phrase structure with proper 4-bar organization
 * 
 * Key improvements:
 * - 4-bar phrase structure (16 notes = 4 bars at 4 notes/bar)
 * - Cadential melody targets (specific scale degrees for phrase endings)
 * - Breath points for natural pauses
 * - Integration with StructureEngine's cadence system
 */
export class PhraseBuilder {
    private phraseState: 'Question' | 'Answer' = 'Question';
    private phraseNoteCount: number = 0;
    private phraseLength: number = 16;  // P2 FIX: 4 bars * 4 notes = 16

    // P2 FIX: Phrase structure tracking
    private currentBar: number = 0;  // 0-3 (4 bars per phrase)
    private currentBeat: number = 0; // 0-3 (4 beats per bar)
    private totalPhrasesCompleted: number = 0;

    // P2 FIX: Track peak position for contour coordination
    private peakNoteIndex: number = 0;
    private highestNoteThisPhrase: number = 0;
    private phrasePeakedHigh: boolean = false;

    constructor() {
        this.reset();
    }

    public reset() {
        this.phraseState = 'Question';
        this.phraseNoteCount = 0;
        this.phraseLength = 16;
        this.currentBar = 0;
        this.currentBeat = 0;
        this.totalPhrasesCompleted = 0;
        this.peakNoteIndex = 0;
        this.highestNoteThisPhrase = 0;
        this.phrasePeakedHigh = false;
    }

    /**
     * P2 FIX: Get phrase position as normalized value (0-1)
     */
    public getPhrasePosition(): number {
        return this.phraseNoteCount / this.phraseLength;
    }

    /**
     * P2 FIX: Get current bar within phrase (0-3)
     */
    public getCurrentBar(): number {
        return this.currentBar;
    }

    /**
     * P2 FIX: Get current beat within bar (0-3)
     */
    public getCurrentBeat(): number {
        return this.currentBeat;
    }

    /**
     * P2 FIX: Check if we're on a strong beat (beat 0 or 2)
     */
    public isStrongBeat(): boolean {
        return this.currentBeat === 0 || this.currentBeat === 2;
    }

    /**
     * P2 FIX: Track peak note for contour coordination
     */
    public trackNote(noteValue: number): void {
        if (noteValue > this.highestNoteThisPhrase) {
            this.highestNoteThisPhrase = noteValue;
            this.peakNoteIndex = this.phraseNoteCount;
        }
    }

    /**
     * P2 FIX: Check if last phrase ended with a high note (for inter-phrase contrast)
     */
    public lastPhraseEndedHigh(): boolean {
        return this.phrasePeakedHigh;
    }

    public advance() {
        this.phraseNoteCount++;
        this.currentBeat++;

        // Advance bar when beat count reaches 4
        if (this.currentBeat >= 4) {
            this.currentBeat = 0;
            this.currentBar++;
        }

        if (this.phraseNoteCount >= this.phraseLength) {
            // Phrase complete
            this.phrasePeakedHigh = this.peakNoteIndex > (this.phraseLength * 0.5);
            this.phraseState = this.phraseState === 'Question' ? 'Answer' : 'Question';
            this.phraseNoteCount = 0;
            this.currentBar = 0;
            this.currentBeat = 0;
            this.totalPhrasesCompleted++;

            // Reset peak tracking for new phrase
            this.highestNoteThisPhrase = 0;
            this.peakNoteIndex = 0;

            // P2 FIX: Vary phrase length slightly for musical interest
            // But keep it in 4-bar units (12, 16, or 20 notes)
            const roll = Math.random();
            if (roll < 0.15) {
                this.phraseLength = 12;  // 3 bars
            } else if (roll < 0.85) {
                this.phraseLength = 16;  // 4 bars (standard)
            } else {
                this.phraseLength = 20;  // 5 bars (extended)
            }
        }
    }

    public isPhraseEnding(): boolean {
        return this.phraseNoteCount >= this.phraseLength - 1;
    }

    /**
     * P2 FIX: Check if we're approaching a phrase cadence (last 4 notes)
     */
    public isApproachingCadence(): boolean {
        return this.phraseNoteCount >= this.phraseLength - 4;
    }

    /**
     * P2 FIX: Get a breath point flag - suggests a rest or held note
     * Breath points occur at:
     * - End of bar 2 (halfway through phrase)
     * - Last beat of phrase (before resolution)
     */
    public getBreathPoint(): boolean {
        // End of bar 2 (note 8 in a 16-note phrase)
        const halfwayPoint = Math.floor(this.phraseLength / 2);
        if (this.phraseNoteCount === halfwayPoint - 1) {
            return Math.random() < 0.4;  // 40% chance of breath
        }

        // Just before final resolution (penultimate note)
        if (this.phraseNoteCount === this.phraseLength - 2) {
            return Math.random() < 0.3;  // 30% chance of breath
        }

        return false;
    }

    /**
     * P2 FIX: Get cadential melody target based on phrase state and cadence type
     * Returns the scale degree (0-6) the melody should aim for at the cadence
     * 
     * @param cadenceType - The type of cadence from StructureEngine
     */
    public getCadentialMelodyTarget(cadenceType: CadenceType = 'none'): number {
        switch (cadenceType) {
            case 'perfect':
                // Perfect authentic cadence: melody MUST land on tonic (1, or scale degree 0)
                return 0;
            case 'imperfect':
                // Imperfect authentic cadence: melody on 3rd or 5th
                return Math.random() < 0.5 ? 2 : 4;
            case 'half':
                // Half cadence: melody can be on 2nd or 7th (leading to V)
                return Math.random() < 0.5 ? 1 : 6;
            case 'plagal':
                // Plagal cadence: melody on tonic or 5th
                return Math.random() < 0.7 ? 0 : 4;
            case 'deceptive':
                // Deceptive cadence: melody on 6th (vi chord)
                return 5;
            case 'none':
            default:
                // No specific target, use phrase state
                return this.phraseState === 'Answer' ? 0 : 2;
        }
    }

    public getResolutionTarget(scaleNotes: number[], chordRootIndex: number, lastMelodyIndex: number, cadenceType: CadenceType = 'none'): number {
        // P2 FIX: Use cadential melody target if a cadence is active
        if (cadenceType !== 'none') {
            const targetDegree = this.getCadentialMelodyTarget(cadenceType);
            const targetIndex = (chordRootIndex + targetDegree) % scaleNotes.length;
            const safeIndex = Math.max(0, Math.min(targetIndex, scaleNotes.length - 1));
            return scaleNotes[safeIndex];
        }

        // Fallback to original logic for non-cadence endings
        // Answer -> Stable (0, 2, 4, 7)
        // Question -> Unstable (1, 6, -1, 3)
        const offsets = this.phraseState === 'Answer'
            ? [0, 2, 4, 7]
            : [1, 6, -1, 3];

        return this.findClosestOffset(scaleNotes, chordRootIndex, lastMelodyIndex, offsets);
    }

    private findClosestOffset(scaleNotes: number[], rootIndex: number, lastMelodyIndex: number, offsets: number[]): number {
        const start = lastMelodyIndex >= 0 ? lastMelodyIndex : rootIndex;

        const candidates = offsets.map(o => rootIndex + o);
        let closestIndex = candidates[0];
        let minDist = Infinity;

        for (const c of candidates) {
            const dist = Math.abs(c - start);
            if (dist < minDist) {
                minDist = dist;
                closestIndex = c;
            }
        }

        // Safety clamp
        const safeIndex = Math.max(0, Math.min(closestIndex, scaleNotes.length - 1));
        return scaleNotes[safeIndex];
    }
}
